import { useEffect } from 'react';
import { router } from 'src';
import { matchPath, useLocation } from 'react-router-dom';
import { Paths } from 'src/pages/paths';
import { safeCallSegment } from '../helpers';
import qs from 'qs';
import {
  trackOpenDocumentSearch,
  trackOpenInAppNotification,
  trackOpenNotificationFromEmail,
  trackOpenProcurementFromEmail,
  trackSwitchCallOffs
} from 'src/segment/events';
import { useTrackEntryWithUTMSource } from './hooks';
import { TenderCatName } from 'src/models/procurements/Tender/types';
import { ProcurementsDisplay } from 'src/models/procurements/types';

function handleDetailsPage(args: { id?: string; qp: qs.ParsedQs }): void {
  const { id, qp } = args;
  const { utm_source, commentId, activeCategory } = qp;
  if (id) {
    analytics.page('Procurement Details', {
      procurementId: id, // backward compatibility
      procurement: {
        id
      }
    });
    if (typeof commentId === 'string' && utm_source === 'email') {
      trackOpenNotificationFromEmail({ id }, { id: commentId });
    } else if (typeof commentId === 'string' && utm_source === 'notification') {
      trackOpenInAppNotification({ id }, { id: commentId });
    } else if (typeof commentId === 'string') {
      trackOpenProcurementFromEmail({ id });
    } else if (activeCategory === TenderCatName.KeywordSearch) {
      trackOpenDocumentSearch({ id });
    }
  }
}

function handleProcurementTablePage(qp: qs.ParsedQs): void {
  const { tab, inbox } = qp;

  if (tab === ProcurementsDisplay.MARKED) {
    analytics.page('Starred view');
  } else if (tab === ProcurementsDisplay.ALL) {
    analytics.page('All tenders view');
  } else if (tab === ProcurementsDisplay.CALL_OFFS && typeof inbox === 'string') {
    trackSwitchCallOffs({ id: inbox });
  }
}

function handlePages(pathname: string, qp: qs.ParsedQs): void {
  const isDetailsPage = matchPath({ path: Paths.TENDER_PAGE }, pathname);
  const isBiddingGPT = matchPath({ path: Paths.BIDDING_GPT }, pathname);
  const isBiddingTool = matchPath({ path: Paths.BIDDING_PAGE }, pathname);
  const isProcurementTablePage = matchPath({ path: Paths.PROCUREMENT_TABLE_PAGE }, pathname);

  if (isProcurementTablePage) {
    handleProcurementTablePage(qp);
  } else if (isDetailsPage) {
    handleDetailsPage({ id: isDetailsPage.params.id, qp });
  } else if (isBiddingGPT) {
    analytics.page('Response Generator');
  } else if (isBiddingTool) {
    analytics.page('Bidding Tool', {
      bidId: isBiddingTool.params.id
    });
  } else {
    analytics.page();
  }
}

export default function usePageWatcher(): void {
  const [trackEntryWithUTMSource] = useTrackEntryWithUTMSource();
  const location = useLocation();
  useEffect(() => {
    const qp = qs.parse(location.search.replace('?', ''));
    const { utm_source } = qp;
    if (typeof utm_source === 'string' && utm_source === 'email') {
      const url = window.location.href;
      trackEntryWithUTMSource(url);
    }
    handlePages(location.pathname, qp);
    const unregister = router.subscribe(state => {
      const qp = qs.parse(state.location.search.replace('?', ''));
      safeCallSegment(() => {
        handlePages(state.location.pathname, qp);
      });
    });

    return () => {
      unregister();
    };
  }, [location.pathname, location.search, trackEntryWithUTMSource]);
}
